import React from 'react';
import type { ColumnType, TablePaginationConfig } from 'antd/es/table';
import { FONT_SIZE, FONT_WEIGHT, ICON_SIZE, SPACING, RADIUS, PAGE_SIZE_OPTIONS, type ThemeTokens } from '../../constants/constants';
import { Card } from '../../types/card';

/** 테이블 컬럼 render에서 값 없을 때 공통으로 쓰는 빈 셀. */
export const emptyCell = (t: ThemeTokens) => <span style={{ color: t.textEmpty }}>-</span>;

/**
 * `<span style={{fontSize:..., color:...}}>{value}</span>` 한 줄짜리 텍스트 컬럼 —
 * CardTable/BuybackPage/NaverSyncLogPage/SuperOverviewPage가 각자 buildColumns()
 * 안에서 이 렌더러를 반복해 만들고 있던 걸 팩토리로 모았다(2026-08-11).
 *
 * 값 자체를 다른 문자열로 바꿔야 하면(날짜 포맷, 라벨 맵 치환, "원" 붙이기 등) `format`,
 * 값에 따라 색이 달라지면(null이면 흐리게 등) `color`에 함수를 넘긴다. 두 콜백 모두
 * `(value, record)`를 받아 각 컬럼이 필요한 만큼만 커스터마이즈하고, 나머지 antd
 * `ColumnType` 속성(width/align/sorter/ellipsis/fixed 등)은 그대로 통과시킨다.
 */
type ColumnExtra<T> = Omit<ColumnType<T>, 'title' | 'dataIndex' | 'render'>;

interface TextColumnOptions<T> extends ColumnExtra<T> {
  /** 값 → 표시할 내용. 기본값은 `value ?? '-'`. */
  format?: (value: any, record: T) => React.ReactNode;
  /** 기본값은 `t.textPrimary`. 값에 따라 달라지면 함수로 넘긴다. */
  color?: string | ((value: any, record: T) => string);
  /** 기본값은 `FONT_SIZE.sm`. */
  fontSize?: number;
  bold?: boolean;
  /** `<span title=...>` — 잘린 텍스트에 대한 HTML 툴팁(예: 실패 사유 전체). */
  cellTitle?: (value: any, record: T) => string | undefined;
}

export function textColumn<T>(
  t: ThemeTokens,
  title: string,
  dataIndex: keyof T & string,
  opts: TextColumnOptions<T> = {},
): ColumnType<T> {
  const { format, color = t.textPrimary, fontSize = FONT_SIZE.sm, bold, cellTitle, ...rest } = opts;
  return {
    title,
    dataIndex,
    ...rest,
    render: (value: any, record: T) => (
      <span
        style={{
          fontSize,
          fontWeight: bold ? FONT_WEIGHT.semibold : undefined,
          color: typeof color === 'function' ? color(value, record) : color,
        }}
        title={cellTitle?.(value, record)}
      >
        {format ? format(value, record) : (value ?? '-')}
      </span>
    ),
  };
}

/** Table의 locale.emptyText 자리에 넣는 공통 빈 상태(아이콘 + 안내문). */
export const TableEmptyState: React.FC<{ icon: string; message: string; t: ThemeTokens; compact?: boolean }> = ({ icon, message, t, compact = false }) => (
  <div style={{ padding: `${compact ? SPACING.xl : SPACING['3xl'] * 2}px 0`, textAlign: 'center' }}>
    <div style={{ fontSize: ICON_SIZE.xxl, marginBottom: SPACING.xs, opacity: 0.25 }}>{icon}</div>
    <div style={{ fontSize: FONT_SIZE.sm, color: t.textMuted }}>{message}</div>
  </div>
);

/**
 * 첫 조회 중 표 자리의 스켈레톤 줄(가격 목록 개편 §2-6, 감사 card-controltower#697) — 스피너만 돌면 표가 어떤 모양일지 모른 채
 * 기다렸다. 재조회는 이것을 쓰지 않고 기존 줄을 남긴다(화면이 깜박이지 않게).
 */
export const TableSkeleton: React.FC<{ rows?: number; t: ThemeTokens; testId?: string }> = ({ rows = 6, t, testId }) => (
  <div role="status" aria-label="목록을 불러오는 중" data-testid={testId} style={{ padding: `${SPACING.sm}px ${SPACING.lg}px` }}>
    {Array.from({ length: rows }, (_, i) => (
      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: SPACING.sm, height: 40, borderBottom: `1px solid ${t.border}` }}>
        <span className="app-skeleton" style={{ width: 32, height: 32, borderRadius: RADIUS.sm, background: t.bgRaised, flex: 'none' }} />
        <span className="app-skeleton" style={{ flex: 1, height: 12, borderRadius: RADIUS.sm, background: t.bgRaised }} />
        <span className="app-skeleton" style={{ width: 72, height: 12, borderRadius: RADIUS.sm, background: t.bgRaised, flex: 'none' }} />
        <span className="app-skeleton" style={{ width: 72, height: 12, borderRadius: RADIUS.sm, background: t.bgRaised, flex: 'none' }} />
      </div>
    ))}
  </div>
);

/** CardTable/StockTable/BuybackPage/SuperOverviewPage가 그대로 반복하던 pagination 설정. */
export function buildPagination(pageSize: number, setPageSize: (size: number) => void): TablePaginationConfig {
  return {
    pageSize,
    onShowSizeChange: (_: number, size: number) => setPageSize(size),
    showSizeChanger: true,
    pageSizeOptions: PAGE_SIZE_OPTIONS,
    placement: ['bottomEnd'],
    // 390px 에서 페이지 번호·크기 변경이 표 오른쪽 밖으로 넘치지 않도록 antd 의 반응형
    // 축소를 켠다(2026-10-20 코덱 3차 지적).
    responsive: true,
  };
}

/** CardTable/StockTable가 바이트 단위로 같았던 썸네일 컬럼(이미지 있으면 이미지, 없으면 플레이스홀더). */
export function thumbnailColumn(t: ThemeTokens): ColumnType<Card> {
  return {
    title: '이미지', key: 'thumbnail', width: 56, align: 'center',
    render: (_: unknown, card: Card) =>
      card.imageUrl ? (
        <img
          src={card.imageUrl} alt={card.productName} className="card-thumbnail"
          style={{ margin: '0 auto' }} loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div style={{ width: 28, height: 39, borderRadius: RADIUS.sm, background: t.bgPlaceholder, margin: '0 auto' }} />
      ),
  };
}
